import { BaseDimensionComponent } from "../models/components/commonComponents/BaseDimensionComponent";
import { DoNotSaveComponent } from "../models/components/commonComponents/DoNotSaveComponent";
import { LayerComponent } from "../models/components/commonComponents/LayerComponent";
import { NamingComponent } from "../models/components/commonComponents/NamingComponent";
import { NotClickableComponent } from "../models/components/commonComponents/NotClickableComponent";
import { ClassCategoryComponent } from "../models/components/containerComponents/ClassCategoryComponent";
import { ContainerBaseComponent } from "../models/components/containerComponents/ContainerBaseComponent";
import { FloorComponent } from "../models/components/containerComponents/FloorComponent";
import { EditorFloor } from "../models/editor-floor.model";
import { EditorObject } from "../models/editor-object.model";
import { AxisEnum } from "../models/enums/AxisEnum";
import { ClassCategoryEnum } from "../models/enums/ClassCategoryEnum";
import { LayerEnum } from "../models/enums/LayerEnum";
import { InteriorObject } from "../models/interior-object.model";
import { Box } from "../models/other/box.model";
import { Variant } from "../models/variant.model";
import { CloneService } from "../services/clone.service";
import { ClassCategoryColorMap } from "./classCategoryColorMap";
import { ComponentNames } from "./componentNames";
import { getEnumKeyByEnumValue } from "./enumKeyByEnumValue";
import { mapStepsToDegree } from "./mapStepToDegree";
import { topLeftObjectCornerPositionToKonvaPosition } from "./positionMapper";

export function changeVariantFloorStructureToEditorVariantFloorStructure(variant: Variant, cloneService: CloneService) {
    let result = {
        parsedVariant: variant,
        floorContainers: []
    };

    let floors = [];
    variant.floors.forEach(floor => {
        var eFloor = new EditorFloor();
        eFloor.floorType = floor.floorType;
        eFloor.elements = new Array();

        let floorInteriorObject = new InteriorObject();
        let floorDoNotSaveComponent = new DoNotSaveComponent();
        let floorNotClickableComponent = new NotClickableComponent();
        let floorBaseDimensionComponent = new BaseDimensionComponent();
        let floorNamingComponent = new NamingComponent();
        floorNamingComponent.name = "FloorContainer";
        let floorLayerComponent = new LayerComponent();
        floorLayerComponent.id = LayerEnum.ground;
        let floorComponent = new FloorComponent();
        floorComponent.floorImageBase64Data = floor.floorImageBase64Data;
        floorComponent.isFloorImageVisible = floor.isFloorImageVisible;
        floorInteriorObject.components = [
            floorDoNotSaveComponent,
            floorNotClickableComponent,
            floorLayerComponent,
            floorComponent,
            floorBaseDimensionComponent
        ];
        let floorObject: EditorObject;
        let backgroundGroup = {
            floor: floor.floorType,
            type: 'BackgroundContainer',
            childs: []
        };
        let classAreaGroup = {
            floor: floor.floorType,
            type: 'ClassContainer',
            childs: []
        };
        // loop to map InteriorObjects in EditorObjects
        floor.elements.forEach((io: InteriorObject) => {
            let intO: InteriorObject = new InteriorObject();
            intO.components = io.components;

            // mappings to be used in the konva representation
            let baseDimensionsComponent = intO.getComponent(ComponentNames.baseDimensionsComponent) as BaseDimensionComponent;
            // map rotation steps to degree
            baseDimensionsComponent.rotation = mapStepsToDegree(baseDimensionsComponent.rotation);
            // since we have a mapping in the save process regardless of object rotation we want to save the top left corner position of an object
            // that's why we need to map the position back to the original konva position
            baseDimensionsComponent.posX = topLeftObjectCornerPositionToKonvaPosition(baseDimensionsComponent.posX, AxisEnum.X, baseDimensionsComponent.box, baseDimensionsComponent.rotation);
            baseDimensionsComponent.posY = topLeftObjectCornerPositionToKonvaPosition(baseDimensionsComponent.posY, AxisEnum.Y, baseDimensionsComponent.box, baseDimensionsComponent.rotation);
            // wagon container
            let containerWithWalls = intO.getComponent(ComponentNames.containerBaseComponent) as ContainerBaseComponent;
            // class container
            let classContainer = intO.getComponent(ComponentNames.classCategoryComponent) as ClassCategoryComponent;

            // do wagon container specific stuff here
            if (containerWithWalls != null) {
                let cw: ContainerBaseComponent = new ContainerBaseComponent();
                cw.WallThickness = containerWithWalls.WallThickness;
                cw.Walls = new Array<InteriorObject>();

                // this step is currently necessary because each InteriorObject in the wagon container (container.Walls property) has an capital C for the property components
                // reason for this issue is currently unclear!
                containerWithWalls.Walls.forEach((element: any) => {
                    let temp = new InteriorObject();
                    temp.components = cloneService.deepClone(element.Components);
                    cw.Walls.push(temp);
                });

                let lengths = [];
                let floorObjectSizeX = 0;
                let floorObjectSizeY = 0;
                let floorObjectSizeZ = 0;
                let floorObjectPosX, floorObjectPosY, floorObjectPosZ, floorObjectRotation = 0;

                // wagon container walls will be placed on the same layer as the wagon container itself just as individual objects
                cw.Walls.forEach((cIo: InteriorObject) => {
                    let bdc = cIo.getComponent(ComponentNames.baseDimensionsComponent) as BaseDimensionComponent;

                    if (!lengths.includes(bdc.box.length)) {
                        lengths.push(bdc.box.length);
                    }

                    // map rotation steps to degree
                    bdc.rotation = mapStepsToDegree(bdc.rotation);

                    // same mapping reason as stated above
                    let posX = topLeftObjectCornerPositionToKonvaPosition(bdc.posX, AxisEnum.X, bdc.box, bdc.rotation);
                    bdc.posX = posX;

                    let posY = topLeftObjectCornerPositionToKonvaPosition(bdc.posY, AxisEnum.Y, bdc.box, bdc.rotation);
                    bdc.posY = posY;

                    var eO = new EditorObject(cIo);
                    eFloor.elements.push(eO);
                });

                floorObjectSizeY = Math.min(...lengths);
                floorObjectSizeX = Math.max(...lengths);
                floorBaseDimensionComponent.box = new Box();
                floorBaseDimensionComponent.box.SetSize(floorObjectSizeY, floorObjectSizeX, floorObjectSizeZ);
                floorBaseDimensionComponent.SetPosition(floorObjectPosX, floorObjectPosY, floorObjectPosZ, floorObjectRotation);
                floorObject = new EditorObject(floorInteriorObject);
                floorObject.opacity(floor.floorImageOpacity);

                let backgroundGroupEntry = {
                    floor: floor.floorType,
                    name: 'Background',
                    editorObject: floorObject,
                };

                backgroundGroup.childs.push(backgroundGroupEntry);

                // the actual wagon area where all other interior is placed
                var containerObject = new EditorObject(intO);
                eFloor.elements.push(containerObject);
                eFloor.elements.push(floorObject);
            }
            // class container specific stuff gos here 
            else if (classContainer != null) {
                var eO = new EditorObject(intO);
                // default opacity of 0.3 not to cover other objects on the canvas
                eO.opacity(0.3);

                let classAreaGroupEntry = {
                    floor: floor.floorType,
                    name: getEnumKeyByEnumValue(ClassCategoryEnum, classContainer.category),
                    editorObject: eO,
                    fillColor: ClassCategoryColorMap.get(classContainer.category),
                };

                classAreaGroup.childs.push(classAreaGroupEntry);

                eFloor.elements.push(eO);
            }
            // all other cases
            else {
                var newEd = new EditorObject(intO);
                eFloor.elements.push(newEd);
            }
        });

        floors.push(eFloor);
        result.floorContainers.push(backgroundGroup, classAreaGroup);
    });

    result.parsedVariant.floors = floors;

    return result;
}